import React, { ComponentProps, useCallback } from 'react';
import PropTypes, { InferProps } from 'prop-types';
import MuiSelect from '@mui/material/Select';
import MuiFormControl from '@mui/material/FormControl';
import MuiInputLabel from '@mui/material/InputLabel';
import MuiMenuItem from '@mui/material/MenuItem';
import {
  Control,
  ControllerRenderProps,
  FieldPath,
  FieldValues,
  UseControllerProps,
} from 'react-hook-form';
import renderController from '../lib/renderController';
import coalesceWithEmptyString from '../lib/coalesceWithEmptyString';

const InputLabel = ({ ...properties }) => <MuiInputLabel {...properties} />;

const selectPropertyTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.arrayOf(PropTypes.number),
  ]),
  inputProps: PropTypes.shape({
    label: PropTypes.string,
  }),
  onChange: PropTypes.func,
  notched: PropTypes.bool,
  multiple: PropTypes.bool,
  input: PropTypes.element,
  children: PropTypes.node,
  renderValue: PropTypes.func,
  MenuProps: PropTypes.shape({}),
  name: PropTypes.string,
  disabled: PropTypes.bool,
  native: PropTypes.bool,
  labelId: PropTypes.string,
};

type SelectProperties = InferProps<
typeof selectPropertyTypes
> & {
  multiple?: boolean;
  input?: React.ReactElement;
  renderValue?: (
    (value: string[]) => React.ReactNode
  ) | undefined;
  sx?: ComponentProps<typeof MuiSelect>['sx'];
};

const Select = React.forwardRef(({
  label,
  value,
  inputProps,
  notched,
  id,
  onChange,
  multiple,
  input,
  children,
  renderValue,
  MenuProps,
  name,
  disabled,
  native,
  labelId,
  ...properties
}: SelectProperties, reference) => (
  <MuiSelect
    native={native ?? true}
    label={label}
    inputProps={{
      label,
      ...((native ?? true)
        ? { 'aria-label': label ?? undefined }
        : {}),
      ...inputProps,
    }}
    notched={notched ?? undefined}
    id={id ?? undefined}
    labelId={labelId ?? undefined}
    onChange={onChange ?? undefined}
    ref={reference}
    input={input}
    renderValue={renderValue}
    MenuProps={MenuProps ?? undefined}
    multiple={multiple}
    value={value as string[] | '' | undefined}
    name={name ?? undefined}
    disabled={disabled ?? undefined}
    {...properties}
  >
    {children}
  </MuiSelect>
));

Select.propTypes = selectPropertyTypes;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const optionPropertyTypes = {
  children: PropTypes.node.isRequired,
  value: PropTypes.string.isRequired,
};

type OptionProperties = InferProps<
typeof optionPropertyTypes
>;

const Option = (
  { ...properties }: OptionProperties,
) => <option {...properties} />;

const optionsPropertyTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }).isRequired,
  ).isRequired,
};

type OptionsProperties = InferProps<
typeof optionsPropertyTypes
>;

const Options = ({ options }: OptionsProperties) => (
  <>
    {options.map(
      ({ value, label }) => (
        <Option
          value={value}
          key={value}
        >
          {label}
        </Option>
      ),
    )}
  </>
);

Options.propTypes = optionsPropertyTypes;

const FormControl = ({ ...properties }) => <MuiFormControl {...properties} />;

const MenuItem = ({ ...properties }) => <MuiMenuItem {...properties} />;

const selectWithControlBasePropertyTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }).isRequired,
  ).isRequired,
  variant: PropTypes.string,
  fullWidth: PropTypes.bool,
  id: PropTypes.string.isRequired,
  label: PropTypes.string,
  shrinkLabel: PropTypes.bool,
  multiple: PropTypes.bool,
  input: PropTypes.node,
  renderValue: PropTypes.func,
  MenuProps: PropTypes.shape({}),
  name: PropTypes.string,
  disabled: PropTypes.bool,
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const selectWithFormControlPropertyTypes = {
  ...selectWithControlBasePropertyTypes,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

type SelectWithFormControlProperties = InferProps<
typeof selectWithFormControlPropertyTypes
> & {
  multiple?: boolean;
  input?: React.ReactElement;
  renderValue?: (
    (value: string[]) => React.ReactNode
  ) | undefined;
};

const SelectWithFormControl = React.forwardRef(({
  variant,
  fullWidth,
  id,
  shrinkLabel,
  label,
  value,
  options,
  onChange,
  multiple,
  input,
  renderValue,
  MenuProps,
  name,
  disabled,
}: SelectWithFormControlProperties, reference) => (
  <FormControl
    variant={variant}
    fullWidth={fullWidth}
    disabled={disabled}
  >
    <InputLabel
      htmlFor={id}
      shrink={shrinkLabel}
    >
      {label}
    </InputLabel>
    <Select
      notched={shrinkLabel}
      id={id}
      label={label}
      value={coalesceWithEmptyString(value)}
      ref={reference}
      onChange={onChange}
      multiple={multiple}
      input={input}
      renderValue={renderValue}
      MenuProps={MenuProps}
      name={name}
      disabled={disabled}
    >
      <Options options={options} />
    </Select>
  </FormControl>
));

const selectWithControllerPropertyTypes = {
  ...selectWithControlBasePropertyTypes,
  rules: PropTypes.shape({}),
  name: PropTypes.string.isRequired,
  control: PropTypes.shape({}).isRequired,
  defaultValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(
      PropTypes.string,
    ),
  ]),
};

type SelectWithControllerProperties<
  T extends FieldValues = FieldValues,
> = Omit<
  InferProps<typeof selectWithControllerPropertyTypes>,
  'control' | 'rules'
> & {
  control: Control<T>;
  rules?: UseControllerProps['rules'];
  multiple?: boolean;
  input?: React.ReactElement;
  renderValue?: (
    (value: string[]) => React.ReactNode
  ) | undefined;
};

const SelectWithController = <T extends FieldValues = FieldValues>({
  rules,
  name,
  control,
  defaultValue,
  variant,
  fullWidth,
  id,
  shrinkLabel,
  label,
  options,
  multiple,
  input,
  renderValue,
  MenuProps,
  disabled,
}: SelectWithControllerProperties<T>) => {
  const render = useCallback(
    ({
      field: { onChange, value, ref },
    }: {

      field: ControllerRenderProps<T, FieldPath<T>>;
    }) => (
      <SelectWithFormControl
        onChange={onChange}
        value={value}
        ref={ref}
        variant={variant}
        fullWidth={fullWidth}
        id={id}
        shrinkLabel={shrinkLabel}
        label={label}
        options={options}
        multiple={multiple}
        input={input}
        renderValue={renderValue}
        MenuProps={MenuProps}
        name={name}
        disabled={disabled}
      />
    ),
    [
      variant,
      fullWidth,
      id,
      shrinkLabel,
      label,
      options,
      multiple,
      input,
      renderValue,
      MenuProps,
      name,
      disabled,
    ],
  );

  return renderController<T>({
    control, name, rules, defaultValue, render,
  });
};

SelectWithController.propTypes = selectWithControllerPropertyTypes;

const MUISelectWithController = <T extends FieldValues = FieldValues>(
  properties: SelectWithControllerProperties<T>,
) => SelectWithController(properties);

export {
  SelectWithController,
  SelectWithFormControl,
  MUISelectWithController,
  Select,
  Option,
  Options,
  FormControl,
  InputLabel,
  MenuItem,
};

export type { SelectChangeEvent } from '@mui/material/Select';
export type { SelectWithControllerProperties };
